/**
 * Travelers Type Definitions
 * Oda bazlı travelers yapısı için type tanımlamaları
 */

/**
 * Oda yapısı
 * Her odada maksimum 3 kişi (yetişkin + çocuk).
 * Maksimum 3 yetişkin; 3 yetişkin seçilirse çocuk eklenemez.
 * Maksimum 1 çocuk (0-2 yaş aralığında bir çocuk varsa ikinci çocuk eklenebilir).
 */
export interface Room {
  adults: number;        // 1-3 arası
  children: number;      // 0-2 arası (max 1 normalde; 0-2 yaş varsa max 2; toplam kişi max 3)
  childAges: number[];   // Çocuk yaşları (0-11 arası, tur tarihine göre validasyon)
}

/**
 * Travelers Preferences (Cookie yapısı)
 * Tour ve Ship için oda bazlı yapı
 */
export interface TravelersPreferences {
  product_type: 'tour' | 'ship'; // Yat şimdilik yok
  departure_date?: string;       // YYYY-MM-DD formatında
  rooms: Room[];                 // Minimum 1, maksimum 10 oda
}

/**
 * Varsayılan Travelers Preferences
 * Not: departure_date dinamik olarak hesaplanır (bugünden 10 gün sonra)
 */
export function getDefaultTravelersPreferences(departureDate?: string): TravelersPreferences {
  return {
    product_type: 'tour',
    departure_date: departureDate,
    rooms: [
      {
        adults: 2,
        children: 0,
        childAges: [],
      },
    ],
  };
}

/**
 * Oda validasyon sonuçları
 */
export interface RoomValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Oda kapasitesi kontrolü
 * Kurallar: max 3 kişi, max 3 yetişkin, 3 yetişkin ise 0 çocuk; max 1 çocuk (0-2 yaş varsa max 2 çocuk).
 */
export function validateRoomCapacity(room: Room): RoomValidationResult {
  const totalPeople = room.adults + room.children;
  const errors: string[] = [];
  const warnings: string[] = [];

  // Maksimum 3 kişi kontrolü
  if (totalPeople > 3) {
    warnings.push('room_max_capacity_warning');
  }

  // Yetişkin kontrolü (1-3)
  if (room.adults < 1 || room.adults > 3) {
    errors.push('room_adults_invalid');
  }

  // 3 yetişkin ise çocuk olmamalı
  if (room.adults === 3 && room.children > 0) {
    warnings.push('room_max_capacity_warning');
  }

  // Çocuk sayısı: max 1 normalde; 0-2 yaş varsa max 2
  const hasInfantOrToddler = room.childAges.some((a) => a >= 0 && a <= 2);
  const maxChildrenAllowed = hasInfantOrToddler ? 2 : 1;
  if (room.children < 0 || room.children > maxChildrenAllowed) {
    errors.push('room_children_invalid');
  }

  // Çocuk yaşları kontrolü
  if (room.children > 0 && room.childAges.length !== room.children) {
    errors.push('room_child_ages_mismatch');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Tüm odalar için validasyon
 */
export function validateAllRooms(rooms: Room[]): RoomValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Minimum 1 oda kontrolü
  if (rooms.length < 1) {
    errors.push('room_min_limit_warning');
  }

  // Maksimum 10 oda kontrolü
  if (rooms.length > 10) {
    warnings.push('room_max_limit_warning');
  }

  // Her oda için validasyon
  rooms.forEach((room, index) => {
    const roomValidation = validateRoomCapacity(room);
    if (!roomValidation.isValid) {
      errors.push(...roomValidation.errors.map(err => `room_${index}_${err}`));
    }
    warnings.push(...roomValidation.warnings.map(warn => `room_${index}_${warn}`));
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
